import React, { useEffect, useMemo, useState, useDeferredValue } from 'react';
import { X, Upload, AlertTriangle, AlertOctagon } from 'lucide-react';
import { Button } from './ui/Button';
import { Toast } from './ui/Toast';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { parseStickerImport, type ImportKind } from '../lib/stickerImportParser';

interface Props {
  open: boolean;
  onClose: () => void;
  currentCounts: Record<string, number>;
  validCodes: Set<string>;
  onApply: (
    updates: Array<{ code: string; count: number }>
  ) => Promise<{ applied: number }>;
}

interface PreviewStats {
  upgraded?: number;
  alreadyOk?: number;
  willSetToOne?: number;
  willZero?: number;
  preservedDuplicates?: number;
  resetDuplicates?: number;
}

export function ImportStickersModal({
  open,
  onClose,
  currentCounts,
  validCodes,
  onApply,
}: Props) {
  const [kind, setKind] = useState<ImportKind | null>(null);
  const [text, setText] = useState('');
  const [applying, setApplying] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) {
      setText('');
      setKind(null);
      setApplying(false);
      setErrorMessage(null);
    }
  }, [open]);

  const deferredText = useDeferredValue(text);

  const parseResult = useMemo(() => {
    if (!kind || !deferredText.trim()) return null;
    return parseStickerImport(deferredText, validCodes, kind);
  }, [deferredText, kind, validCodes]);

  const currentOwnedCount = useMemo(
    () => Object.values(currentCounts).filter((c) => c >= 1).length,
    [currentCounts]
  );

  const buildResult = useMemo((): {
    updates: Array<{ code: string; count: number }>;
    preview: PreviewStats;
  } => {
    if (!parseResult || !kind) return { updates: [], preview: {} };

    const updates: Array<{ code: string; count: number }> = [];

    if (kind === 'repetidas') {
      let upgraded = 0;
      let alreadyOk = 0;
      parseResult.parsed.forEach((desired, code) => {
        const current = currentCounts[code] || 0;
        const final = Math.max(current, desired);
        if (final > current) {
          updates.push({ code, count: final });
          upgraded++;
        } else {
          alreadyOk++;
        }
      });
      return { updates, preview: { upgraded, alreadyOk } };
    }

    if (kind === 'tenho') {
      const tenhoSet = new Set(parseResult.parsed.keys());
      let willSetToOne = 0;
      let willZero = 0;
      let resetDuplicates = 0;
      let alreadyOk = 0;
      validCodes.forEach((code) => {
        const current = currentCounts[code] || 0;
        if (tenhoSet.has(code)) {
          if (current === 0) {
            updates.push({ code, count: 1 });
            willSetToOne++;
          } else if (current === 1) {
            alreadyOk++;
          } else {
            updates.push({ code, count: 1 });
            resetDuplicates++;
          }
        } else {
          if (current > 0) {
            updates.push({ code, count: 0 });
            willZero++;
          }
        }
      });
      return { updates, preview: { willSetToOne, willZero, resetDuplicates, alreadyOk } };
    }

    // faltantes-reverse
    const faltantesSet = new Set(parseResult.parsed.keys());
    let willZero = 0;
    let willSetToOne = 0;
    let preservedDuplicates = 0;
    validCodes.forEach((code) => {
      const current = currentCounts[code] || 0;
      if (faltantesSet.has(code)) {
        if (current !== 0) {
          updates.push({ code, count: 0 });
          willZero++;
        }
      } else {
        if (current === 0) {
          updates.push({ code, count: 1 });
          willSetToOne++;
        } else if (current >= 2) {
          preservedDuplicates++;
        }
      }
    });
    return { updates, preview: { willZero, willSetToOne, preservedDuplicates } };
  }, [parseResult, kind, currentCounts, validCodes]);

  const handleApply = async () => {
    if (buildResult.updates.length === 0) return;
    setApplying(true);
    setErrorMessage(null);
    try {
      const { applied } = await onApply(buildResult.updates);
      setToastMessage(
        applied === 1 ? '1 figurinha atualizada' : `${applied} figurinhas atualizadas`
      );
      onClose();
    } catch (err) {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Não foi possível aplicar a importação. Tente novamente.'
      );
      setApplying(false);
    }
  };

  if (!open) {
    return toastMessage ? (
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
    ) : null;
  }

  const unrecognizedPreview = parseResult?.unrecognized.slice(0, 10) ?? [];
  const unrecognizedExtras = (parseResult?.unrecognized.length ?? 0) - unrecognizedPreview.length;

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Importar lista de figurinhas"
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6"
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-lg flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-lg max-h-[90svh] sm:max-h-[90vh] animate-fade-up"
        >
          {/* Header fixo — sempre visível */}
          <div className="flex-shrink-0 flex items-start gap-3 px-6 pt-6 pb-4 border-b border-ink-100">
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-semibold text-h2 text-ink-900 mb-1">
                Importar lista
              </h2>
              <p className="font-body text-small text-ink-500">
                Cole sua lista de figurinhas de outro app e a gente preenche o álbum pra você.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="flex-shrink-0 p-2 rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-100"
            >
              <X size={18} />
            </button>
          </div>

          {/* Corpo scrollável */}
          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">

          {/* Seletor de modo */}
          <div className="flex flex-col gap-2 mb-4">
            <button
              type="button"
              aria-pressed={kind === 'tenho'}
              onClick={() => setKind('tenho')}
              className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 transition-colors text-left btn-focus ${
                kind === 'tenho'
                  ? 'border-ink-700 bg-ink-100'
                  : 'border-ink-300 bg-white hover:border-ink-700'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p
                  className={`font-semibold text-small ${
                    kind === 'tenho' ? 'text-ink-900' : 'text-ink-700'
                  }`}
                >
                  Tenho
                </p>
                <p className="text-[12px] text-ink-500 mt-0.5">
                  Lista completa das figurinhas que eu tenho. O app marca todas como tendo 1 e zera o resto. Qualquer x2/x3 na lista é ignorado.
                </p>
              </div>
            </button>

            <button
              type="button"
              aria-pressed={kind === 'repetidas'}
              onClick={() => setKind('repetidas')}
              className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 transition-colors text-left btn-focus ${
                kind === 'repetidas'
                  ? 'border-green-700 bg-green-100'
                  : 'border-ink-300 bg-white hover:border-green-700'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p
                  className={`font-semibold text-small ${
                    kind === 'repetidas' ? 'text-green-900' : 'text-ink-700'
                  }`}
                >
                  Repetidas
                </p>
                <p className="text-[12px] text-ink-500 mt-0.5">
                  Lista das minhas repetidas pra trocar. Formato BoraTrocar: x2 significa 2 a mais que a original.
                </p>
              </div>
            </button>

            <button
              type="button"
              aria-pressed={kind === 'faltantes-reverse'}
              onClick={() => setKind('faltantes-reverse')}
              className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 transition-colors text-left btn-focus ${
                kind === 'faltantes-reverse'
                  ? 'border-gold-500 bg-gold-100'
                  : 'border-ink-300 bg-white hover:border-gold-500'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p
                  className={`font-semibold text-small ${
                    kind === 'faltantes-reverse' ? 'text-ink-900' : 'text-ink-700'
                  }`}
                >
                  Faltantes
                </p>
                <p className="text-[12px] text-ink-500 mt-0.5">
                  Lista das que faltam pra mim. O app vai marcar todo o resto do álbum como tendo.
                </p>
              </div>
            </button>

            {(kind === 'tenho' || kind === 'faltantes-reverse') && (
              <div className="flex items-start gap-2 bg-gold-100 border border-gold-500 rounded-md p-2.5 text-small text-ink-900">
                <AlertTriangle size={15} className="shrink-0 mt-0.5 text-gold-500" />
                <span>
                  {kind === 'tenho'
                    ? 'Essa opção substitui o estado inteiro do álbum. Figurinhas fora da lista serão zeradas.'
                    : 'Essa opção marca como tendo TODAS as figurinhas que não estão na sua lista. Use só se a lista colada está completa e atualizada.'}
                </span>
              </div>
            )}

            {(kind === 'tenho' || kind === 'faltantes-reverse') && currentOwnedCount >= 50 && (
              <div className="flex items-start gap-2 bg-ruby-100 border border-ruby-500 rounded-md p-2.5 text-small text-ruby-700">
                <AlertOctagon size={15} className="shrink-0 mt-0.5" />
                <span>
                  Você já tem {currentOwnedCount} figurinhas marcadas. Certifique-se de que a lista colada está completa para não perder dados.
                </span>
              </div>
            )}
          </div>

          {/* Formatos suportados */}
          <details className="mb-4 text-small text-ink-700">
            <summary className="cursor-pointer font-semibold text-ink-900 select-none">
              Ver formatos suportados
            </summary>
            <div className="mt-3 space-y-3 font-mono text-[12px] leading-snug bg-ink-100 rounded-md p-3 text-ink-700">
              <div>
                <p className="font-sans font-semibold text-ink-900 mb-1">
                  Modo Tenho (lista completa)
                </p>
                BRA1, BRA2, ARG5, FWC1, MEX10
              </div>
              <div>
                <p className="font-sans font-semibold text-ink-900 mb-1">
                  Modo Repetidas (padrão BoraTrocar)
                </p>
                BRA1, ARG5 x3, MEX10 x2
              </div>
              <div>
                <p className="font-sans font-semibold text-ink-900 mb-1">
                  Modo Repetidas (com parenteses)
                </p>
                MEX 14(×1), KOR 13(×3)
              </div>
              <div>
                <p className="font-sans font-semibold text-ink-900 mb-1">
                  Modo Faltantes (com bandeira)
                </p>
                MEX 🇲🇽: 1, 2, 3, 4<br />
                FWC 🏆: 00
              </div>
              <div>
                <p className="font-sans font-semibold text-ink-900 mb-1">
                  Modo Faltantes (códigos diretos)
                </p>
                BRA1, BRA2, ARG5, FWC10
              </div>
            </div>
          </details>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            maxLength={50000}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={!kind}
            placeholder={
              !kind
                ? 'Escolha primeiro o tipo de lista acima'
                : kind === 'tenho'
                ? 'Cole sua lista de figurinhas que você tem aqui...\n\nEx: BRA1, BRA2, ARG5, FWC1\n(x2, x3 são ignorados neste modo)'
                : kind === 'repetidas'
                ? 'Cole sua lista de repetidas aqui...\n\nEx: BRA1, ARG5 x3, MEX10 x2'
                : 'Cole sua lista de faltantes aqui...\n\nEx: BRA1, BRA2, BRA3'
            }
            aria-label="Lista de figurinhas para importar"
            className={`w-full rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 font-mono p-3 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring resize-y${
              !kind ? ' opacity-50 cursor-not-allowed bg-ink-100' : ''
            }`}
          />

          <div className="mt-4" aria-live="polite">
            {kind && !text.trim() && (
              <p className="font-body text-small text-ink-500">
                Cole sua lista para ver o que será importado.
              </p>
            )}

            {kind && text.trim() && parseResult && (
              <div className="space-y-2 text-small">
                {parseResult.conflictingHeader && (
                  <div className="flex items-start gap-2 bg-gold-100 border border-gold-500 rounded-md p-2.5 text-ink-900">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <span>
                      Você marcou como{' '}
                      {kind === 'repetidas' ? 'Repetidas' : 'Faltantes'} mas a lista tem
                      cabeçalho "
                      {parseResult.conflictingHeader === 'faltantes' ? 'Faltantes' : 'Repetidas'}
                      ". Tem certeza?
                    </span>
                  </div>
                )}

                {kind === 'tenho' && parseResult.parsed.size > 0 && (
                  <p className="text-ink-700">
                    <span className="font-semibold text-ink-900">
                      {buildResult.preview.willSetToOne ?? 0}
                    </span>{' '}
                    serão marcadas como tendo.{' '}
                    <span className="font-semibold text-ink-900">
                      {buildResult.preview.willZero ?? 0}
                    </span>{' '}
                    zeradas.
                    {(buildResult.preview.resetDuplicates ?? 0) > 0 && (
                      <>
                        {' '}
                        <span className="font-semibold text-ink-900">
                          {buildResult.preview.resetDuplicates}
                        </span>{' '}
                        repetidas reduzidas para 1.
                      </>
                    )}
                  </p>
                )}

                {kind === 'repetidas' && (
                  <>
                    {buildResult.updates.length > 0 ? (
                      <p className="text-ink-900">
                        <span className="font-semibold text-green-900">
                          {buildResult.updates.length}
                        </span>{' '}
                        {buildResult.updates.length === 1
                          ? 'figurinha será atualizada'
                          : 'figurinhas serão atualizadas'}{' '}
                        como repetidas.
                      </p>
                    ) : (
                      parseResult.parsed.size > 0 && (
                        <p className="text-ink-500">
                          Nada a aplicar - seus counts já estão no nível pedido.
                        </p>
                      )
                    )}
                    {(buildResult.preview.alreadyOk ?? 0) > 0 && (
                      <p className="text-ink-500">
                        {buildResult.preview.alreadyOk}{' '}
                        {buildResult.preview.alreadyOk === 1
                          ? 'figurinha já no nível pedido'
                          : 'figurinhas já no nível pedido'}{' '}
                        (nada a fazer).
                      </p>
                    )}
                  </>
                )}

                {kind === 'faltantes-reverse' && parseResult.parsed.size > 0 && (
                  <p className="text-ink-700">
                    <span className="font-semibold text-ink-900">
                      {buildResult.preview.willSetToOne ?? 0}
                    </span>{' '}
                    marcadas como tendo.{' '}
                    <span className="font-semibold text-ink-900">
                      {buildResult.preview.willZero ?? 0}
                    </span>{' '}
                    zeradas.{' '}
                    <span className="font-semibold text-ink-900">
                      {buildResult.preview.preservedDuplicates ?? 0}
                    </span>{' '}
                    repetidas atuais preservadas.
                  </p>
                )}

                {parseResult.unrecognized.length > 0 && (
                  <details className="text-ink-500">
                    <summary className="cursor-pointer select-none">
                      <span className="font-semibold">{parseResult.unrecognized.length}</span>{' '}
                      {parseResult.unrecognized.length === 1
                        ? 'código não reconhecido'
                        : 'códigos não reconhecidos'}
                    </summary>
                    <p className="mt-2 font-mono text-[12px] text-ink-700 break-words">
                      {unrecognizedPreview.join(', ')}
                      {unrecognizedExtras > 0 && ` ...e mais ${unrecognizedExtras}`}
                    </p>
                  </details>
                )}

                {parseResult.parsed.size === 0 && parseResult.unrecognized.length === 0 && (
                  <p className="text-ink-500">
                    Nenhum código de figurinha encontrado na lista.
                  </p>
                )}
              </div>
            )}

          </div>
          </div>{/* fecha corpo scrollável */}

          {/* Footer fixo — sempre visível */}
          <div className="flex-shrink-0 px-6 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] border-t border-ink-100">
            {errorMessage && (
              <div className="mb-3 bg-ruby-100 border border-ruby-500 rounded-md p-2.5 text-small text-ruby-700">
                {errorMessage}
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button variant="ghost" size="md" onClick={onClose} disabled={applying}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={Upload}
                onClick={handleApply}
                loading={applying}
                disabled={buildResult.updates.length === 0}
              >
                {buildResult.updates.length > 0
                  ? `Aplicar (${buildResult.updates.length})`
                  : 'Aplicar'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </>
  );
}
